// Income tables of the case brief: grouped income rows, adjustment items, filing comparison.
// Moved unchanged out of CaseBriefPage.js (ticket 072).
import React from 'react'
import { formatEuro } from '../../../lib/briefUtils'
import { INCOME_CARD_CONFIG } from '../../../hooks/incomeCardConfig'

const REVIEW_FLAG_LABELS = {
  treaty_review:                  'Treaty review',
  foreign_retirement_account:     'Foreign retirement acct.',
  tax_advantaged_account_review:  'Tax-advantaged acct.',
  entity_classification_review:   'Entity classification',
  foreign_company_distribution:   'Foreign company dist.',
  pass_through_income_review:     'Pass-through review',
  foreign_government_pension:     'Govt. pension (treaty)',
  foreign_tax_relief:             'Foreign tax relief',
  owner_operated_entity:          'Owner-operated entity',
  partial_year_residency:         'Partial-year residency',
  currency_conversion:            'Currency conversion',
  property_sale_exemption:        'Primary residence exemption?',
  rental_reduction_eligibility:   'Rental reduction (60%)?',
  crypto_reporting_review:        'Crypto reporting',
  professional_review_required:  'Needs review',
  us_roth_treatment:              'Roth treatment (contested)',
  us_llc_k1_classification:       'LLC / K-1 (two scenarios)',
}

// Derived at import time from incomeCardConfig.js's INCOME_CARD_CONFIG —
// the same canonical source IncomeCard.js's subtype dropdown reads
// (cardConfig.subtypeOptions) — instead of a hand-typed mirror. This
// closes the drift the two used to have (tickets/017-incomedetail):
// roughly half the entries had diverged, several losing real meaning
// (e.g. a foreign pension label that could read as government when it
// meant the opposite).
//
// other_foreign_retirement is the one real IncomeSubtype (calculatorTypes.ts)
// with no incomeCardConfig.js dropdown entry — it's assigned by the
// classifier (incomeClassificationRules.ts), never chosen by the user, so
// it can't be derived from the flatten. Kept as an explicit override so
// items carrying it still get a real label instead of falling through to
// the raw-value auto-fallback.
const INCOME_SUBTYPE_LABELS = {
  ...Object.fromEntries(
    INCOME_CARD_CONFIG.flatMap(c => c.subtypeOptions || []).map(o => [o.value, o.label])
  ),
  other_foreign_retirement: 'Other foreign retirement acct.',
}

// Fallback for items with no income_subtype (subtype is optional — see
// calculatorTypes.ts) — mirrors the IncomeType enum. Without this, a
// user-entered label like "Chase Bank" or "Slava" tells an advisor
// nothing about what kind of income it actually is; income_type is always
// present even when subtype isn't, so it's the right floor to fall back
// to rather than showing nothing.
//
// Derived from incomeCardConfig.js the same way as INCOME_SUBTYPE_LABELS
// above (same drift class, same fix). 'interest' has no dedicated card in
// incomeCardConfig.js (folded into the 'dividends' card's UI copy,
// "Dividends and interest") but is still a real IncomeType the rules
// engine matches on (incomeClassificationRules.ts:268) — kept as an
// explicit override for the same reason as other_foreign_retirement above.
const INCOME_TYPE_FALLBACK_LABELS = {
  ...Object.fromEntries(INCOME_CARD_CONFIG.map(c => [c.income_type, c.label])),
  interest: 'Interest / savings',
}

// self_employment items have no entry in the IncomeSubtype enum —
// activity_classification is their real "more specifically" fact.
const ACTIVITY_CLASSIFICATION_LABELS = {
  profesional:                   'Profesional activity',
  empresarial:                   'Empresarial activity',
  agricola_ganadera_forestal:    'Agrícola / ganadera / forestal activity',
}

// Foreign tax paid is in the item's original currency, not EUR — showing
// a bare "€X" would misrepresent it. Shown as a plain labeled amount
// instead of running it through formatEuro.
function formatForeignTaxPaid(amount, precision) {
  if (precision === 'unknown') return 'foreign tax paid: unknown'
  if (!amount) return null
  return `${amount.toLocaleString('en-US')} foreign tax paid`
}

// Grouped by tax base at the call site — this table just renders one
// group's rows, no classification column (it's the group heading now).
// Metadata line: subtype (e.g. "US LLC / K-1"), source country, taxes
// already paid (foreign + Spain withholding), then review flags — mirrors
// what generate-case-brief now forwards from ClassifiedIncomeItem.
export function GroupedIncomeTable({ items }) {
  const subtotal = items.reduce((sum, item) => sum + (item.amount_eur || 0), 0)
  return (
    <div className="brief-findings-wrap">
      <table className="brief-findings-table brief-income-table">
        <tbody>
          {items.map((item, i) => {
            const flagLabels = (item.review_flags || []).map(f => REVIEW_FLAG_LABELS[f] || f.replace(/_/g, ' '))
            // "More specifically" descriptor — always show something, even
            // when the item has no income_subtype and the user's own label
            // is ambiguous ("Chase Bank," "Slava"). self_employment items
            // use activity_classification instead (subtype doesn't cover
            // them); everything else falls back to income_type.
            const specificLabel = item.income_type === 'self_employment'
              ? (item.activity_classification
                  ? (ACTIVITY_CLASSIFICATION_LABELS[item.activity_classification] || item.activity_classification.replace(/_/g, ' '))
                  : (INCOME_TYPE_FALLBACK_LABELS.self_employment))
              : item.income_subtype
                ? (INCOME_SUBTYPE_LABELS[item.income_subtype] || item.income_subtype.replace(/_/g, ' '))
                : (INCOME_TYPE_FALLBACK_LABELS[item.income_type] || item.income_type?.replace(/_/g, ' ') || null)
            const metaParts = [
              specificLabel,
              item.source_country || null,
              formatForeignTaxPaid(item.tax_paid_foreign, item.tax_paid_precision),
              item.tax_withheld_spain > 0 ? `${formatEuro(item.tax_withheld_spain)} withheld in Spain` : null,
              // Ticket 072: extra meta lines an intake brief adds (work location, as entered, started this year).
              ...(item.extra_meta || []),
            ].filter(Boolean)
            return (
              <tr key={i} className="brief-findings-tr">
                <td className="brief-findings-td">
                  {item.label}
                  {metaParts.length > 0 && <div className="u-desc-xs">{metaParts.join(' · ')}</div>}
                  {flagLabels.length > 0 && <div className="u-desc-xs">{flagLabels.join(', ')}</div>}
                </td>
                <td className="brief-findings-td brief-findings-td-nowrap">{formatEuro(item.amount_eur)}</td>
              </tr>
            )
          })}
        </tbody>
        {items.length > 1 && (
          <tfoot>
            <tr className="brief-findings-tr brief-findings-total-row">
              <td className="brief-findings-td"><strong>Subtotal</strong></td>
              <td className="brief-findings-td brief-findings-td-nowrap"><strong>{formatEuro(subtotal)}</strong></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

// Only rendered when display_mode === 'adjustment_estimate' — the headline
// irpf_gross is a conservative baseline, and each of these is a specific,
// deterministic condition that would lower it if it holds. Deterministic
// engine output (irpfCore.ts), same standing as everything else on this
// page — not a recommendation, just what the engine already computed.
export function AdjustmentItemsTable({ items }) {
  if (!items || items.length === 0) return null
  return (
    <div className="brief-adjustments">
      <div className="brief-fact-group-title">Potential reductions to this baseline</div>
      {items.map((a, i) => (
        <div key={i} className="brief-adjustment-row">
          <div className="brief-adjustment-top">
            <span className="brief-adjustment-label">{a.label}</span>
            <span className="brief-adjustment-reduction">−{formatEuro(a.potential_reduction)}</span>
          </div>
          <div className="u-desc-xs">{a.condition}</div>
          {a.advisor_note && <div className="brief-adjustment-note">{a.advisor_note}</div>}
        </div>
      ))}
    </div>
  )
}

// Only present when the calculator ran the individual-vs-joint comparison
// (married + at least one spouse-tagged item) — see filingComparison.ts.
// Mirrors the consumer result page's FilingComparisonCard: recommendation
// first, then both household totals, with the individual total broken
// into "your return + spouse's return" so it doesn't read as a third,
// disagreeing number next to the headline estimate.
export function FilingComparisonNote({ comparison }) {
  if (!comparison) return null
  const jointRecommended = comparison.recommended_mode === 'joint_orientation_v1'
  const hasSplit = comparison.self_irpf_gross != null && comparison.spouse_irpf_gross != null

  return (
    <div className="brief-filing-comparison">
      <div className="brief-fact-group-title">Filing comparison</div>
      <div className="brief-fact-row">
        <span className="brief-fact-label">Recommended</span>
        <span className="brief-fact-value">{jointRecommended ? 'Filing jointly' : 'Filing individually'}</span>
      </div>
      <div className="brief-fact-row">
        <span className="brief-fact-label">Filing individually</span>
        <span className="brief-fact-value">
          {formatEuro(comparison.household_tax_individual)}
          {hasSplit && (
            <span className="u-desc-xs"> (You: {formatEuro(comparison.self_irpf_gross)} + spouse: {formatEuro(comparison.spouse_irpf_gross)})</span>
          )}
        </span>
      </div>
      <div className="brief-fact-row">
        <span className="brief-fact-label">Filing jointly</span>
        <span className="brief-fact-value">{formatEuro(comparison.household_tax_joint)}</span>
      </div>
      {comparison.savings_vs_alternative > 0 && (
        <div className="brief-fact-row">
          <span className="brief-fact-label">Savings from recommended mode</span>
          <span className="brief-fact-value">{formatEuro(comparison.savings_vs_alternative)}</span>
        </div>
      )}
      {comparison.assumptions?.length > 0 && (
        <div className="u-desc-xs brief-filing-comparison-assumptions">{comparison.assumptions.join(' ')}</div>
      )}
    </div>
  )
}
